/**
 * User context attached to Better Stack Errors events.
 *
 * Deliberately narrow: a stable internal id and the role, nothing else. That
 * is enough to answer the two questions an incident actually raises — "how
 * many people hit this?" and "is it one role or everyone?" — without putting
 * email addresses or names into the error store, which has a different
 * retention and access model than our database.
 *
 * The id is our internal UUID (`users.id`), not the Auth0 subject, so it joins
 * to PostHog (which identifies on the same id) and to API logs.
 *
 * Sentry's scope is global and survives every route change, so identity is
 * sticky until something overwrites it. Each function below therefore writes
 * ALL THREE fields, never a subset: whichever one runs last fully describes
 * the session, so no ordering of calls can leave a stale id or tag behind.
 */
import * as Sentry from '@sentry/react'

export interface ErrorUserContext {
  id: string
  role?: string | null
}

export function setErrorUser({ id, role }: ErrorUserContext) {
  Sentry.setUser({ id })
  // Also a tag: alert rules and issue filters work on tags, not on user fields.
  Sentry.setTag('user_role', role ?? 'unknown')
  Sentry.setTag('impersonated', undefined)
}

/**
 * Impersonation: attribute the error to nobody, and say so.
 *
 * Neither party is the right answer. The admin is not who the error happened
 * to, so counting them inflates their impact and hides that it was seen in
 * someone else's data; the impersonated user never experienced it, so naming
 * them reports user impact that did not occur — and "affected users" is a
 * number we intend to alert on. The tag keeps these events searchable and
 * stops them reading as ordinary logged-out traffic. It carries no id, so it
 * adds nothing sensitive to the error store.
 */
export function setImpersonatedErrorSession() {
  Sentry.setUser(null)
  Sentry.setTag('user_role', undefined)
  Sentry.setTag('impersonated', 'true')
}

/** No identified session: logged out, or the profile is gone for any reason. */
export function clearErrorUser() {
  Sentry.setUser(null)
  Sentry.setTag('user_role', undefined)
  Sentry.setTag('impersonated', undefined)
}
